import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns';
import { VotingType } from '../../common/core-api';
import { UserType } from '../../common/enums/UserType';
import { type IdeaRow, MODERATION_STATUS } from './IdeaCard.types';

export interface CommonCardFlags {
  isAnonymous: boolean;
  hasAccess: boolean;
  canModerate: boolean;
  isFlagged: boolean;
  isArchived: boolean;
  isPrivate: boolean;
  isHidden: boolean;
}

export const getCommonCardFlags = (idea: IdeaRow, hideUnModerated: boolean): CommonCardFlags => {
  const challenge = idea.challenge;
  return {
    isAnonymous: idea.isAnonymous === 1,
    hasAccess: idea.viewerPermissions?.hasAccess ?? false,
    canModerate: idea.viewerPermissions?.canModerate ?? false,
    isFlagged: idea.hasReports === 1,
    isArchived: (challenge?.archived ?? 0) === 1,
    isPrivate: challenge?.isPrivateSubmission ?? false,
    isHidden: hideUnModerated && (idea.hasReports === 1 || idea.moderationStatus === MODERATION_STATUS.REMOVED)
  };
};

export const getVoteLabel = (votingType?: VotingType | null): string => {
  switch (votingType) {
    case VotingType.POINTSAWARD:
      return 'Points';
    case VotingType.SCORECARD:
      return 'Points';
    case VotingType.STARS:
      return 'Rating';
    default:
      return 'Score';
  }
};

export const getDisplayVoteValue = (voteCount: number | null, score: number | null): number => {
  return score ?? voteCount ?? 0;
};

export interface TimeLeftResult {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  ongoing: boolean;
  expired: boolean;
}

export const computeTimeLeft = (createdAt: string, daysLimit: string | null | undefined): TimeLeftResult | null => {
  if (!daysLimit) return null;
  const limitDays = parseInt(daysLimit, 10);
  if (isNaN(limitDays) || limitDays <= 0) return null;

  const created = new Date(createdAt);
  if (isNaN(created.getTime())) return null;

  const deadline = new Date(created.getTime() + limitDays * 24 * 60 * 60 * 1000);
  const now = new Date();

  if (now >= deadline) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, ongoing: false, expired: true };
  }

  const days = differenceInDays(deadline, now);
  const hours = differenceInHours(deadline, now) % 24;
  const minutes = differenceInMinutes(deadline, now) % 60;
  const seconds = differenceInSeconds(deadline, now) % 60;
  return { days, hours, minutes, seconds, ongoing: true, expired: false };
};

export const USER_TYPE_LABELS: Partial<Record<number, string>> = {
  [UserType.SUPER_ADMIN]: 'Admin',
  [UserType.ADMIN]: 'Admin',
  [UserType.MODERATOR]: 'Moderator',
  [UserType.SUPPORT]: 'Support',
  [UserType.ENGINEER]: 'Engineer'
};

export const openUserProfilePopup = (userId: string, profileLabel: string, focusTarget: HTMLElement): void => {
  const topWindow = window.top ?? window;
  const topPopUp = (topWindow as typeof window & { popUp?: (url: string, options: unknown) => void }).popUp;
  if (topPopUp) {
    topPopUp(`/user/view/${userId}`, {
      popUpTitle: profileLabel,
      height: 390,
      focusOnClose: focusTarget
    });
  }
};
